import type { OidcConfig } from '@/lib/env'

/**
 * Minimal, dependency-free OIDC client for a Keycloak **public** client using
 * Authorization Code + PKCE (S256). No implicit flow, no client secret.
 *
 * Deliberately hand-rolled: the surface we need is small (discover, authorize,
 * exchange, refresh, end-session) and this keeps the whole auth path readable
 * and fully typed.
 */

const TOKENS_KEY = 'saltseed.auth.tokens'
const TX_KEY = 'saltseed.auth.tx'
const DISCOVERY_KEY = 'saltseed.auth.discovery'

/** Refresh this many milliseconds before the access token actually expires. */
export const REFRESH_SKEW_MS = 60_000

export interface TokenSet {
  access_token: string
  refresh_token: string | undefined
  id_token: string | undefined
  /** Absolute expiry as epoch milliseconds. */
  expires_at: number
  scope: string | undefined
}

export interface IdTokenClaims {
  sub: string | undefined
  email: string | undefined
  name: string | undefined
  preferred_username: string | undefined
}

export interface DiscoveryDocument {
  issuer: string
  authorization_endpoint: string
  token_endpoint: string
  end_session_endpoint: string | undefined
  userinfo_endpoint: string | undefined
}

export class AuthError extends Error {
  readonly code: string

  constructor(code: string, message: string) {
    super(message)
    this.name = 'AuthError'
    this.code = code
  }
}

/* -------------------------------------------------------------------------- */
/* Encoding helpers                                                           */
/* -------------------------------------------------------------------------- */

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomUrlSafe(byteLength: number): string {
  const bytes = new Uint8Array(byteLength)
  crypto.getRandomValues(bytes)
  return base64UrlEncode(bytes)
}

async function s256Challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64UrlEncode(new Uint8Array(digest))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readString(source: Record<string, unknown>, key: string): string | undefined {
  const value = source[key]
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/* -------------------------------------------------------------------------- */
/* Discovery                                                                  */
/* -------------------------------------------------------------------------- */

let discoveryCache: DiscoveryDocument | undefined

function readCachedDiscovery(issuer: string): DiscoveryDocument | undefined {
  if (discoveryCache?.issuer === issuer) return discoveryCache
  const raw = sessionStorage.getItem(DISCOVERY_KEY)
  if (raw === null) return undefined
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed)) return undefined
    const doc = toDiscoveryDocument(parsed)
    if (doc?.issuer !== issuer) return undefined
    discoveryCache = doc
    return doc
  } catch {
    return undefined
  }
}

function toDiscoveryDocument(value: Record<string, unknown>): DiscoveryDocument | undefined {
  const issuer = readString(value, 'issuer')
  const authorization_endpoint = readString(value, 'authorization_endpoint')
  const token_endpoint = readString(value, 'token_endpoint')
  if (issuer === undefined || authorization_endpoint === undefined || token_endpoint === undefined) {
    return undefined
  }
  return {
    issuer,
    authorization_endpoint,
    token_endpoint,
    end_session_endpoint: readString(value, 'end_session_endpoint'),
    userinfo_endpoint: readString(value, 'userinfo_endpoint'),
  }
}

export async function discover(config: OidcConfig): Promise<DiscoveryDocument> {
  const cached = readCachedDiscovery(config.issuer)
  if (cached !== undefined) return cached

  const url = `${config.issuer}/.well-known/openid-configuration`
  let response: Response
  try {
    response = await fetch(url, { headers: { Accept: 'application/json' } })
  } catch {
    throw new AuthError(
      'discovery_unreachable',
      `Could not reach the identity provider at ${config.issuer}.`,
    )
  }
  if (!response.ok) {
    throw new AuthError(
      'discovery_failed',
      `Identity provider metadata returned HTTP ${response.status}. Check VITE_KEYCLOAK_ISSUER.`,
    )
  }

  const parsed: unknown = await response.json()
  if (!isRecord(parsed)) {
    throw new AuthError('discovery_malformed', 'Identity provider metadata was malformed.')
  }
  const doc = toDiscoveryDocument(parsed)
  if (doc === undefined) {
    throw new AuthError(
      'discovery_malformed',
      'Identity provider metadata is missing required endpoints.',
    )
  }

  discoveryCache = doc
  sessionStorage.setItem(DISCOVERY_KEY, JSON.stringify(doc))
  return doc
}

/* -------------------------------------------------------------------------- */
/* Token storage                                                              */
/* -------------------------------------------------------------------------- */

function toTokenSet(value: unknown): TokenSet | undefined {
  if (!isRecord(value)) return undefined
  const access_token = readString(value, 'access_token')
  const expires_at = value['expires_at']
  if (access_token === undefined || typeof expires_at !== 'number') return undefined
  return {
    access_token,
    refresh_token: readString(value, 'refresh_token'),
    id_token: readString(value, 'id_token'),
    expires_at,
    scope: readString(value, 'scope'),
  }
}

/**
 * Tokens live in `sessionStorage`: they survive a reload of the same tab but
 * not a browser restart, and are never shared across tabs or origins.
 */
export function loadTokens(): TokenSet | undefined {
  const raw = sessionStorage.getItem(TOKENS_KEY)
  if (raw === null) return undefined
  try {
    return toTokenSet(JSON.parse(raw))
  } catch {
    return undefined
  }
}

export function saveTokens(tokens: TokenSet): void {
  sessionStorage.setItem(TOKENS_KEY, JSON.stringify(tokens))
}

export function clearTokens(): void {
  sessionStorage.removeItem(TOKENS_KEY)
}

export function isExpired(tokens: TokenSet, skewMs = REFRESH_SKEW_MS): boolean {
  return Date.now() >= tokens.expires_at - skewMs
}

/* -------------------------------------------------------------------------- */
/* ID token claims                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Decodes the ID token payload for display purposes only. Signature validation
 * is the backend's job — the SPA never makes a trust decision on these claims.
 */
export function decodeIdToken(idToken: string | undefined): IdTokenClaims | undefined {
  if (idToken === undefined) return undefined
  const segment = idToken.split('.')[1]
  if (segment === undefined) return undefined
  try {
    const padded = segment.replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    )
    const parsed: unknown = JSON.parse(json)
    if (!isRecord(parsed)) return undefined
    return {
      sub: readString(parsed, 'sub'),
      email: readString(parsed, 'email'),
      name: readString(parsed, 'name'),
      preferred_username: readString(parsed, 'preferred_username'),
    }
  } catch {
    return undefined
  }
}

/* -------------------------------------------------------------------------- */
/* Authorization request                                                      */
/* -------------------------------------------------------------------------- */

interface AuthTransaction {
  state: string
  nonce: string
  code_verifier: string
  redirect_uri: string
  /** In-app path to return to once the callback completes. */
  return_to: string
}

function saveTransaction(tx: AuthTransaction): void {
  sessionStorage.setItem(TX_KEY, JSON.stringify(tx))
}

function takeTransaction(): AuthTransaction | undefined {
  const raw = sessionStorage.getItem(TX_KEY)
  sessionStorage.removeItem(TX_KEY)
  if (raw === null) return undefined
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed)) return undefined
    const state = readString(parsed, 'state')
    const nonce = readString(parsed, 'nonce')
    const code_verifier = readString(parsed, 'code_verifier')
    const redirect_uri = readString(parsed, 'redirect_uri')
    if (
      state === undefined ||
      nonce === undefined ||
      code_verifier === undefined ||
      redirect_uri === undefined
    ) {
      return undefined
    }
    return {
      state,
      nonce,
      code_verifier,
      redirect_uri,
      return_to: readString(parsed, 'return_to') ?? '/app',
    }
  } catch {
    return undefined
  }
}

/** Builds the authorization URL and hands control to Keycloak. */
export async function beginLogin(
  config: OidcConfig,
  options: { returnTo?: string; prompt?: 'login' | 'none'; loginHint?: string } = {},
): Promise<void> {
  const doc = await discover(config)

  const codeVerifier = randomUrlSafe(48)
  const tx: AuthTransaction = {
    state: randomUrlSafe(24),
    nonce: randomUrlSafe(24),
    code_verifier: codeVerifier,
    redirect_uri: config.redirectUri,
    return_to: options.returnTo ?? '/app',
  }
  saveTransaction(tx)

  const url = new URL(doc.authorization_endpoint)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('client_id', config.clientId)
  url.searchParams.set('redirect_uri', tx.redirect_uri)
  url.searchParams.set('scope', config.scope)
  url.searchParams.set('state', tx.state)
  url.searchParams.set('nonce', tx.nonce)
  url.searchParams.set('code_challenge', await s256Challenge(codeVerifier))
  url.searchParams.set('code_challenge_method', 'S256')
  if (options.prompt !== undefined) url.searchParams.set('prompt', options.prompt)
  if (options.loginHint !== undefined) url.searchParams.set('login_hint', options.loginHint)

  window.location.assign(url.toString())
}

/* -------------------------------------------------------------------------- */
/* Token endpoint                                                             */
/* -------------------------------------------------------------------------- */

async function postToken(
  doc: DiscoveryDocument,
  body: URLSearchParams,
): Promise<TokenSet> {
  let response: Response
  try {
    response = await fetch(doc.token_endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
      body: body.toString(),
    })
  } catch {
    throw new AuthError('token_unreachable', 'Could not reach the identity provider.')
  }

  const parsed: unknown = await response.json().catch(() => undefined)

  if (!response.ok) {
    const code = isRecord(parsed) ? (readString(parsed, 'error') ?? 'token_error') : 'token_error'
    const description = isRecord(parsed) ? readString(parsed, 'error_description') : undefined
    throw new AuthError(code, description ?? `Token request failed (HTTP ${response.status}).`)
  }

  if (!isRecord(parsed)) {
    throw new AuthError('token_malformed', 'Token response was malformed.')
  }

  const accessToken = readString(parsed, 'access_token')
  if (accessToken === undefined) {
    throw new AuthError('token_malformed', 'Token response contained no access token.')
  }

  const expiresIn = parsed['expires_in']
  const lifetimeSeconds = typeof expiresIn === 'number' && expiresIn > 0 ? expiresIn : 300

  return {
    access_token: accessToken,
    refresh_token: readString(parsed, 'refresh_token'),
    id_token: readString(parsed, 'id_token'),
    expires_at: Date.now() + lifetimeSeconds * 1000,
    scope: readString(parsed, 'scope'),
  }
}

export interface CallbackResult {
  tokens: TokenSet
  returnTo: string
}

/**
 * Completes the redirect: validates `state`, exchanges `code` for tokens.
 * Reads the query string from `search`.
 */
export async function completeLogin(
  config: OidcConfig,
  search: string,
): Promise<CallbackResult> {
  const params = new URLSearchParams(search)

  const error = params.get('error')
  if (error !== null) {
    throw new AuthError(error, params.get('error_description') ?? `Sign-in failed: ${error}.`)
  }

  const code = params.get('code')
  const state = params.get('state')
  if (code === null || state === null) {
    throw new AuthError('invalid_callback', 'The sign-in response was incomplete.')
  }

  const tx = takeTransaction()
  if (tx === undefined) {
    throw new AuthError(
      'missing_transaction',
      'This sign-in link is no longer valid. Start the sign-in again.',
    )
  }
  if (tx.state !== state) {
    throw new AuthError('state_mismatch', 'Sign-in state did not match. Start the sign-in again.')
  }

  const doc = await discover(config)
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: tx.redirect_uri,
    code_verifier: tx.code_verifier,
  })

  return { tokens: await postToken(doc, body), returnTo: tx.return_to }
}

/** Exchanges a refresh token for a fresh token set. */
export async function refreshTokens(
  config: OidcConfig,
  refreshToken: string,
): Promise<TokenSet> {
  const doc = await discover(config)
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: config.clientId,
    refresh_token: refreshToken,
  })
  const next = await postToken(doc, body)
  // Keycloak normally rotates the refresh token; keep the old one if it did not.
  return next.refresh_token === undefined ? { ...next, refresh_token: refreshToken } : next
}

/** Redirects to the IdP end-session endpoint, or home if there isn't one. */
export async function endSession(
  config: OidcConfig,
  idToken: string | undefined,
): Promise<void> {
  clearTokens()
  let endSessionEndpoint: string | undefined
  try {
    endSessionEndpoint = (await discover(config)).end_session_endpoint
  } catch {
    endSessionEndpoint = undefined
  }

  if (endSessionEndpoint === undefined) {
    window.location.assign(config.postLogoutRedirectUri)
    return
  }

  const url = new URL(endSessionEndpoint)
  url.searchParams.set('post_logout_redirect_uri', config.postLogoutRedirectUri)
  url.searchParams.set('client_id', config.clientId)
  if (idToken !== undefined) url.searchParams.set('id_token_hint', idToken)
  window.location.assign(url.toString())
}
